import React, { useRef } from 'react';
import { extractConfig, validateConfig, applyConfig, getDefaultConfig } from '../utils/configExport';
import useLocalization from '../hooks/useLocalization';

/**
 * Component for importing and exporting calculator configuration
 */
const ConfigurationIO = ({ state, onConfigApply }) => {
  const translations = useLocalization();
  const fileInputRef = useRef(null);
  
  // Text strings with defaults in case localization is not set up
  const {
    configIO = {
      title: "Configuration",
      exportButton: "Export Configuration",
      importButton: "Import Configuration",
      resetButton: "Reset to Defaults",
      exportSuccess: "Configuration exported successfully",
      importSuccess: "Configuration imported successfully",
      importError: "Error importing configuration",
      resetSuccess: "Configuration reset to defaults",
      fileName: "startup-config.json"
    }
  } = translations;

  /**
   * Handle exporting the current configuration
   */
  const handleExport = () => {
    try {
      // Extract the current configuration
      const config = extractConfig(state);
      
      // Convert to JSON
      const configJson = JSON.stringify(config, null, 2);
      
      // Create download link
      const blob = new Blob([configJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      // Create and click download link
      const a = document.createElement('a');
      a.href = url;
      a.download = configIO.fileName;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      alert(configIO.exportSuccess);
    } catch (error) {
      console.error('Export error:', error);
      alert(`${configIO.importError}: ${error.message}`);
    }
  };

  /**
   * Handle importing a configuration
   */
  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  /**
   * Process the imported file
   */
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const config = JSON.parse(e.target.result);
        
        // Validate the configuration
        const validation = validateConfig(config);
        if (!validation.valid) {
          alert(`${configIO.importError}: ${validation.message}`);
          return;
        }
        
        // Apply the configuration
        const stateUpdates = applyConfig(config);
        onConfigApply(stateUpdates);
        
        alert(configIO.importSuccess);
      } catch (error) {
        console.error('Import error:', error);
        alert(`${configIO.importError}: ${error.message}`);
      }
    };
    
    reader.readAsText(file);
    
    // Reset the file input to allow selecting the same file again
    event.target.value = null;
  };

  /**
   * Reset to default configuration
   */
  const handleResetToDefaults = () => {
    try {
      const defaultConfig = getDefaultConfig();
      const stateUpdates = applyConfig(defaultConfig);
      onConfigApply(stateUpdates);
      
      alert(configIO.resetSuccess);
    } catch (error) {
      console.error('Reset error:', error);
      alert(`Error resetting configuration: ${error.message}`);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-6">
      <h2 className="text-xl font-semibold mb-4">{configIO.title}</h2>
      
      <div className="flex flex-wrap gap-2">
        <button
          onClick={handleExport}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md"
        >
          {configIO.exportButton}
        </button>
        
        <button
          onClick={handleImportClick}
          className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md"
        >
          {configIO.importButton}
        </button>
        
        <button
          onClick={handleResetToDefaults}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md"
        >
          {configIO.resetButton}
        </button>
        
        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".json"
          className="hidden"
        />
      </div>
    </div>
  );
};

export default ConfigurationIO;